/**
 * Impact Rating Editor Component
 *
 * Allows tenants to customize damage scenario impact rating criteria and examples
 * for Safety, Financial, Operational, and Privacy categories.
 */

import React, { useState } from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { Icon } from '../../../components/ui/Icon';
import { Button } from '../../../components/ui/Button2';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Switch } from '../../../components/ui/Switch';
import { getProgressColor } from '../../../utils/progressColor';
import { getImpactColor } from '../../../utils/severityStyle';
import styles from './ImpactRatingEditor.module.css';

const RATING_LEVELS = ['Severe', 'Major', 'Moderate', 'Negligible'];
const CATEGORIES = ['Safety', 'Financial', 'Operational', 'Privacy'];

const getCategoryColor = (category) => {
  const colors = {
    Safety: '#ef4444',
    Financial: '#10b981',
    Operational: '#3b82f6',
    Privacy: '#8b5cf6'
  };
  return colors[category] || '#6b7280';
};

const getRatingColor = (level) => getImpactColor(level, { scheme: 'fourTier' });

export default function ImpactRatingEditor() {
  const { config, updateConfiguration } = useConfiguration();
  const [selectedCategory, setSelectedCategory] = useState('Safety');
  const [selectedLevel, setSelectedLevel] = useState('Severe');
  const [expandedSections, setExpandedSections] = useState({
    settings: true,
    editor: true
  });

  if (!config?.configuration?.impact_rating_config) {
    return (
      <div className={styles.container}>
        <div className={styles.noData}>
          <Icon name="warning" size={40} className={styles.noDataIcon} />
          <p>No impact rating configuration available</p>
        </div>
      </div>
    );
  }

  const ratingConfig = config.configuration.impact_rating_config;

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const handleToggleCustom = (enabled) => {
    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          use_custom_definitions: enabled
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const handleCalculationMethodChange = (method) => {
    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          final_rating_calculation: method,
          category_weights: method === 'WEIGHTED_AVERAGE'
            ? (ratingConfig.category_weights || { Safety: 0.4, Financial: 0.2, Operational: 0.2, Privacy: 0.2 })
            : ratingConfig.category_weights
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const handleWeightChange = (category, value) => {
    const weight = parseFloat(value);
    if (isNaN(weight) || weight < 0 || weight > 1) return;

    const updatedWeights = {
      ...(ratingConfig.category_weights || {}),
      [category]: weight
    };

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          category_weights: updatedWeights
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const getCurrentDefinition = () => {
    return ratingConfig.definitions?.find(
      (def) => def.rating_level === selectedLevel && def.category === selectedCategory
    );
  };

  const handleCriteriaChange = (newCriteria) => {
    const currentDef = getCurrentDefinition();
    let updatedDefinitions;

    if (currentDef) {
      updatedDefinitions = ratingConfig.definitions.map((def) =>
        def.rating_level === selectedLevel && def.category === selectedCategory
          ? { ...def, criteria: newCriteria }
          : def
      );
    } else {
      updatedDefinitions = [
        ...(ratingConfig.definitions || []),
        {
          rating_level: selectedLevel,
          category: selectedCategory,
          criteria: newCriteria,
          examples: [],
          monetary_range: selectedCategory === 'Financial' ? '' : null
        }
      ];
    }

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          definitions: updatedDefinitions
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const handleExampleChange = (index, newValue) => {
    const currentDef = getCurrentDefinition();
    if (!currentDef) return;

    const updatedExamples = [...currentDef.examples];
    updatedExamples[index] = newValue;

    const updatedDefinitions = ratingConfig.definitions.map((def) =>
      def.rating_level === selectedLevel && def.category === selectedCategory
        ? { ...def, examples: updatedExamples }
        : def
    );

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          definitions: updatedDefinitions
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const handleAddExample = () => {
    const currentDef = getCurrentDefinition();
    if (!currentDef) return;

    if (currentDef.examples.length >= 10) {
      alert('Maximum 10 examples allowed per definition');
      return;
    }

    const updatedExamples = [...currentDef.examples, ''];

    const updatedDefinitions = ratingConfig.definitions.map((def) =>
      def.rating_level === selectedLevel && def.category === selectedCategory
        ? { ...def, examples: updatedExamples }
        : def
    );

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          definitions: updatedDefinitions
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const handleRemoveExample = (index) => {
    const currentDef = getCurrentDefinition();
    if (!currentDef) return;

    const updatedExamples = currentDef.examples.filter((_, i) => i !== index);

    const updatedDefinitions = ratingConfig.definitions.map((def) =>
      def.rating_level === selectedLevel && def.category === selectedCategory
        ? { ...def, examples: updatedExamples }
        : def
    );

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          definitions: updatedDefinitions
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const handleMonetaryRangeChange = (newRange) => {
    const currentDef = getCurrentDefinition();
    if (!currentDef) return;

    const updatedDefinitions = ratingConfig.definitions.map((def) =>
      def.rating_level === selectedLevel && def.category === selectedCategory
        ? { ...def, monetary_range: newRange }
        : def
    );

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        impact_rating_config: {
          ...ratingConfig,
          definitions: updatedDefinitions
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  const currentDef = getCurrentDefinition();
  const totalWeight = ratingConfig.category_weights
    ? Object.values(ratingConfig.category_weights).reduce((sum, w) => sum + w, 0)
    : 0;

  const completenessPercentage = Math.round(
    (ratingConfig.definitions?.length || 0) / (RATING_LEVELS.length * CATEGORIES.length) * 100
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2>Impact Rating Configuration</h2>
      </div>

      {/* Settings Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('settings')}>
          <h3 className={styles.sectionHeading}>Global Settings</h3>
          <Icon name={expandedSections.settings ? 'chevron-down' : 'chevron-right'} size={14} />
        </div>

        {expandedSections.settings && (
          <div className={styles.sectionContent}>
            {/* Custom Definitions Toggle */}
            <div className={styles.settingCard}>
              <div className={styles.settingInfo}>
                <label className={styles.settingLabel}>Use Custom Criteria</label>
                <p className={styles.settingDescription}>
                  Enable to override ISO 21434 defaults with your organization's specific rating criteria
                </p>
              </div>
              <Switch
                checked={ratingConfig.use_custom_definitions || false}
                onCheckedChange={handleToggleCustom}
                aria-label="Use custom criteria"
              />
            </div>

            {ratingConfig.use_custom_definitions && (
              <>
                {/* Calculation Method */}
                <div className={styles.settingCard}>
                  <div className={styles.settingInfo}>
                    <label className={styles.settingLabel}>Final Rating Calculation</label>
                    <p className={styles.settingDescription}>
                      How to calculate final impact from category ratings
                    </p>
                  </div>
                  <select
                    value={ratingConfig.final_rating_calculation || 'MAX'}
                    onChange={(e) => handleCalculationMethodChange(e.target.value)}
                    className={styles.select}
                  >
                    <option value="MAX">Maximum (Highest category rating)</option>
                    <option value="WEIGHTED_AVERAGE">Weighted Average</option>
                  </select>
                </div>

                {/* Category Weights */}
                {ratingConfig.final_rating_calculation === 'WEIGHTED_AVERAGE' && (
                  <div className={styles.weightsCard}>
                    <h4>Category Weights (must sum to 1.0)</h4>
                    <div className={styles.weightsGrid}>
                      {CATEGORIES.map((category) => (
                        <div key={category} className={styles.weightRow}>
                          <span className={styles.weightLabel}>{category}</span>
                          <Input
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            value={ratingConfig.category_weights?.[category] || 0}
                            onChange={(e) => handleWeightChange(category, e.target.value)}
                            className={styles.weightInput}
                          />
                        </div>
                      ))}
                    </div>
                    <div className={`${styles.weightTotal} ${Math.abs(totalWeight - 1.0) < 0.01 ? styles.valid : styles.invalid}`}>
                      {Math.abs(totalWeight - 1.0) < 0.01 ? (
                        <>
                          <Icon name="success" size={14} />
                          <span>Total: {totalWeight.toFixed(2)}</span>
                        </>
                      ) : (
                        <>
                          <Icon name="warning" size={14} />
                          <span>Total: {totalWeight.toFixed(2)} (Must equal 1.0)</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Completeness Indicator */}
                <div className={styles.completenessRow}>
                  <span className={styles.completenessLabel}>Definition Completeness:</span>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{
                        width: `${completenessPercentage}%`,
                        backgroundColor: getProgressColor(completenessPercentage, { scheme: 'completeness' })
                      }}
                    />
                  </div>
                  <span className={styles.completenessCount}>
                    {ratingConfig.definitions?.length || 0}/{RATING_LEVELS.length * CATEGORIES.length} definitions configured
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Editor Section */}
      {ratingConfig.use_custom_definitions && (
        <div className={styles.section}>
          <div className={styles.sectionHeader} onClick={() => toggleSection('editor')}>
            <h3 className={styles.sectionHeading}>Criteria Editor</h3>
            <Icon name={expandedSections.editor ? 'chevron-down' : 'chevron-right'} size={14} />
          </div>

          {expandedSections.editor && (
            <div className={styles.sectionContent}>
              {/* Category Selector */}
              <div className={styles.selectorGroup}>
                <label className={styles.selectorLabel}>Select Category</label>
                <div className={styles.categoryGrid}>
                  {CATEGORIES.map((category) => {
                    const color = getCategoryColor(category);
                    const isActive = selectedCategory === category;
                    return (
                      <button
                        key={category}
                        className={`${styles.categoryButton} ${isActive ? styles.active : ''}`}
                        onClick={() => setSelectedCategory(category)}
                        style={isActive ? { borderColor: color, backgroundColor: `${color}15`, color } : undefined}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Rating Level Selector */}
              <div className={styles.selectorGroup}>
                <label className={styles.selectorLabel}>Select Rating Level</label>
                <div className={styles.levelGrid}>
                  {RATING_LEVELS.map((level) => {
                    const color = getRatingColor(level);
                    const isActive = selectedLevel === level;
                    return (
                      <button
                        key={level}
                        className={`${styles.levelButton} ${isActive ? styles.active : ''}`}
                        onClick={() => setSelectedLevel(level)}
                        style={isActive ? { borderColor: color, backgroundColor: `${color}15`, color } : undefined}
                      >
                        <div className={styles.levelDot} style={{ backgroundColor: color }} />
                        <span>{level}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Criteria Input */}
                  <div className={styles.formGroup}>
                    <label htmlFor="criteria" className={styles.formLabel}>
                      Rating Criteria
                    </label>
                    <Textarea
                      id="criteria"
                      value={currentDef?.criteria || ''}
                      onChange={(e) => handleCriteriaChange(e.target.value)}
                      placeholder="Describe what qualifies for this rating level in natural language. Be specific about severity, scope, and consequences. (10-1000 characters)"
                      maxLength={1000}
                      rows={4}
                    />
                    <span className={styles.charCount}>
                      {currentDef?.criteria?.length || 0}/1000 characters
                    </span>
                    {currentDef?.criteria && currentDef.criteria.length < 10 && (
                      <p className={styles.validationError}>
                        <Icon name="warning" size={12} />
                        Criteria must be at least 10 characters
                      </p>
                    )}
                  </div>

                  {/* Monetary Range */}
                  {selectedCategory === 'Financial' && (
                    <div className={styles.formGroup}>
                      <label htmlFor="monetaryRange" className={styles.formLabel}>
                        Monetary Range (optional)
                      </label>
                      <Input
                        id="monetaryRange"
                        type="text"
                        value={currentDef?.monetary_range || ''}
                        onChange={(e) => handleMonetaryRangeChange(e.target.value)}
                        placeholder="e.g., >$10M, $100K-$10M, <$1K"
                      />
                    </div>
                  )}

                  {/* Examples */}
                  <div className={styles.formGroup}>
                    <div className={styles.examplesHeader}>
                      <label className={styles.formLabel}>
                        Example Scenarios (optional, max 10)
                      </label>
                      <Button
                        variant="outline"
                        size="xs"
                        startIcon={<Icon name="add" size={14} />}
                        onClick={handleAddExample}
                        disabled={!currentDef || (currentDef?.examples?.length || 0) >= 10}
                      >
                        Add Example
                      </Button>
                    </div>

                    {currentDef?.examples && currentDef.examples.length > 0 ? (
                      <div className={styles.examplesList}>
                        {currentDef.examples.map((example, index) => (
                          <div key={index} className={styles.exampleItem}>
                            <span className={styles.exampleNumber}>{index + 1}</span>
                            <Input
                              type="text"
                              value={example}
                              onChange={(e) => handleExampleChange(index, e.target.value)}
                              placeholder={`Example ${index + 1}`}
                              className={styles.exampleInput}
                            />
                            <Button
                              variant="destructive"
                              size="icon-sm"
                              onClick={() => handleRemoveExample(index)}
                              title="Remove example"
                              aria-label="Remove example"
                            >
                              <Icon name="close" size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.noExamples}>
                        <Icon name="info" size={24} className={styles.noExamplesIcon} />
                        <p>No examples added yet. Click &quot;Add Example&quot; to add scenarios.</p>
                      </div>
                    )}
                  </div>
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      <InstructionPanel title="How to Use">
        <ul>
          <li><strong>Enable Custom Criteria:</strong> Toggle "Use Custom Criteria" to override ISO 21434 defaults</li>
          <li><strong>Define Each Combination:</strong> Provide criteria for all 16 combinations (4 levels × 4 categories)</li>
          <li><strong>Write Clear Criteria:</strong> Use natural language (10-1000 chars). The AI uses these to assess damage scenarios</li>
          <li><strong>Add Examples:</strong> Provide concrete scenarios (max 10) to guide the AI's understanding</li>
          <li><strong>Financial Ranges:</strong> Specify monetary ranges for Financial category (e.g., {'">$10M"'}, "$100K-$10M")</li>
          <li><strong>Calculation Method:</strong> Choose MAX (highest category) or WEIGHTED_AVERAGE (with custom weights)</li>
          <li><strong>Save Changes:</strong> Click "Save Changes" in the header to create a new configuration version</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}
